import * as Sentry from '@sentry/browser';
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import ConfigApi from '../config/api';
import { buildConfig, setConfig as setDeprecatedConfig, setLanguage } from '../config/config';
import { AppConfigType, BackendConfigType } from '../config/types';
import AppRoutes from '../routes';
import useFetch from '../utils/hooks/useFetch';
import { AppConfigContext } from './AppConfigContext';
import GlobalError from './GlobalError';
import GlobalLoader from './GlobalLoader';

type AppLocale = {
    dayjs: 'en' | 'ru';
    antd: 'en_US' | 'ru_RU';
};

export const resolveAppLocale = (language: unknown): AppLocale => {
    if (typeof language !== 'string') {
        return { dayjs: 'ru', antd: 'ru_RU' };
    }

    switch (language.trim().replace('-', '_').toLowerCase()) {
        case 'en':
        case 'en_us':
            return { dayjs: 'en', antd: 'en_US' };
        case 'ru':
        case 'ru_ru':
            return { dayjs: 'ru', antd: 'ru_RU' };
        default:
            return { dayjs: 'ru', antd: 'ru_RU' };
    }
};

const App: FC = () => {
    const [fetch, loading, error] = useFetch(ConfigApi.getConfig);
    const [config, setConfig] = useState<AppConfigType>();

    useEffect(() => {
        fetch()
            .then((resp) => {
                const cfg = buildConfig(resp as BackendConfigType);
                cfg.setConfig = (remote) => {
                    setConfig((currentConfig) =>
                        currentConfig === undefined ? currentConfig : { ...currentConfig, remote }
                    );
                };
                setConfig(cfg);
                setDeprecatedConfig(cfg);
            })
            .catch(() => undefined);
    }, [fetch]);

    if (error) {
        return <GlobalError />;
    }

    if (loading || config === undefined) {
        return <GlobalLoader />;
    }

    Sentry.setContext('config', config);
    const locale = resolveAppLocale(config.remote.language);
    setLanguage(locale.dayjs); //Hack for old translate function

    dayjs.extend(require('dayjs/plugin/relativeTime'));
    dayjs.extend(require('dayjs/plugin/localizedFormat'));
    require('dayjs/locale/' + locale.dayjs + '.js');
    dayjs.locale(locale.dayjs);
    const antdLocale = require('antd/es/locale/' + locale.antd + '.js').default;

    dayjs.extend((option, dayjsClass, dayjsFactory) => {
        dayjsClass.prototype.toJSON = function () {
            return this.format(config.remote.tenantConfig.formats.dateTime);
        };
    });

    return (
        <AppConfigContext.Provider value={config}>
            <ConfigProvider locale={antdLocale}>
                <AppRoutes />
            </ConfigProvider>
        </AppConfigContext.Provider>
    );
};

export default App;
